import React, { useState } from "react";
import { API_ENDPOINTS } from "../config";
import { useApp } from "../context/AppContext";
import QuestionnairePage from "./features/QuestionnairePage";
import ProfileImportScreenNew from "./ProfileImportScreenNew";
import EmailVerificationScreen from "./EmailVerificationScreen";
import ProfilePhotoUpload from "./ProfilePhotoUpload";
import { VocreaLogo } from "./VocreaLogo";
import { ImageWithFallback } from "./ImageWithFallback";
import "../styles/register-new.css";

interface RegisterScreenProps {
  onRegisterSuccess: () => void;
  onSwitchToLogin: () => void;
}

interface Option {
  value: string;
  label: string;
}

const accountTypes: Option[] = [
  { value: "normal", label: "Regular User" },
  { value: "therapist", label: "Therapist" },
];

const RegisterScreenNew: React.FC<RegisterScreenProps> = ({
  onRegisterSuccess,
  onSwitchToLogin,
}) => {
  const { initializeSession } = useApp();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    type: "normal",
    name: "",
  });

  const [validationErrors, setValidationErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    type: "",
    name: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showProfileImport, setShowProfileImport] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showProfilePhoto, setShowProfilePhoto] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");

  // Form validation
  const validateForm = () => {
    const errors = {
      email: "",
      password: "",
      confirmPassword: "",
      type: "",
      name: "",
    };

    if (!formData.name.trim()) {
      errors.name = "Nickname is required";
    } else if (formData.name.length > 20) {
      errors.name = "Nickname cannot exceed 20 characters";
    }

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return (
      !errors.email &&
      !errors.password &&
      !errors.confirmPassword &&
      !errors.type &&
      !errors.name
    );
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    setError(null);
  };

  const handleTypeSelect = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      type: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    // Register user immediately (both normal and therapist)
    setIsLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.REGISTER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          type: formData.type,
          name: formData.name,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Registration failed");

      // Show email verification screen for all user types
      setVerificationEmail(formData.email);
      setShowVerification(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueToQuestionnaire = () => {
    setShowConfirmDialog(false);

    if (formData.type === "therapist") {
      setShowProfileImport(true);
    } else {
      setShowQuestionnaire(true);
    }
  };

  const handleVerificationComplete = async (token: string) => {
    try {
      // Store token - user is not fully logged in yet
      localStorage.setItem("token", token);

      setShowVerification(false);

      // For therapists, show profile import/questionnaire after verification
      // They will be fully logged in after completing the questionnaire
      if (formData.type === "therapist") {
        setShowProfileImport(true);
      } else {
        // For normal users, show profile photo upload then complete login
        setShowProfilePhoto(true);
      }
    } catch (error: any) {
      console.error("Failed to initialize session:", error);
      setError(error.message || "Failed to initialize session");
    }
  };

  const handleProfilePhotoComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      // Use shared session initialization logic from AppContext
      // connectWebSocket=true will establish WebSocket connection for real-time features
      await initializeSession(token, true);

      // Don't call onRegisterSuccess() - the state updates above will trigger
      // App.tsx to automatically render the main screen since user is now logged in
    } catch (error: any) {
      console.error('Failed to complete login:', error);
      setError(error.message || 'Failed to complete registration');
    }
  };

  const handleSkipProfilePhoto = () => {
    // Same as handleProfilePhotoComplete - reload to trigger session restoration
    handleProfilePhotoComplete();
  };

  const handleResendCode = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.RESEND_VERIFICATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verificationEmail }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to resend verification code");
      }
    } catch (err: any) {
      console.error("Resend verification failed:", err.message);
    }
  };

  const handleBackFromVerification = () => {
    setShowVerification(false);
    setVerificationEmail("");
  };

  // Show profile photo upload after verification
  if (showProfilePhoto) {
    return (
      <ProfilePhotoUpload
        onComplete={handleProfilePhotoComplete}
        onSkip={handleSkipProfilePhoto}
      />
    );
  }

  // Show verification screen if needed
  if (showVerification) {
    return (
      <EmailVerificationScreen
        email={verificationEmail}
        onVerified={handleVerificationComplete}
        onResend={handleResendCode}
        onBack={handleBackFromVerification}
      />
    );
  }

  // Show profile import screen for therapists
  if (showProfileImport) {
    return (
      <ProfileImportScreenNew
        userType={formData.type}
        onBack={() => setShowProfileImport(false)}
        onManualEntry={() => {
          setShowProfileImport(false);
          setShowQuestionnaire(true);
        }}
      />
    );
  }

  // Show questionnaire for manual entry
  if (showQuestionnaire) {
    return (
      <QuestionnairePage
        userType={formData.type}
        onBack={() => setShowQuestionnaire(false)}
        onRegisterSuccess={onRegisterSuccess}
        registrationData={{
          email: formData.email,
          password: formData.password,
          name: formData.name,
        }}
      />
    );
  }

  return (
    <div className="register-new-screen">
      {/* Left Side - Sign Up Form */}
      <div className="register-new-left">
        <div className="register-new-form-container">
          {/* Logo and Header */}
          <div className="register-new-header">
            <div className="register-new-logo-title">
              <VocreaLogo />
              <h1 className="register-new-title">Vocrea AI</h1>
            </div>
            <h2 className="register-new-subtitle">Create your account</h2>
            <p className="register-new-description">Start your journey to emotional wellness</p>
          </div>

          {/* Sign Up Form */}
          <form onSubmit={handleSubmit} className="register-new-form">
            <div className="register-new-form-group">
              <label htmlFor="name" className="register-new-label">
                Nickname
              </label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Enter your nickname"
                value={formData.name}
                onChange={handleChange}
                className="register-new-input"
                disabled={isLoading}
                required
              />
              {validationErrors.name && (
                <span className="register-new-error">{validationErrors.name}</span>
              )}
            </div>

            <div className="register-new-form-group">
              <label htmlFor="email" className="register-new-label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="register-new-input"
                disabled={isLoading}
                required
              />
              {validationErrors.email && (
                <span className="register-new-error">{validationErrors.email}</span>
              )}
            </div>

            <div className="register-new-form-group">
              <label htmlFor="password" className="register-new-label">
                Password
              </label>
              <div className="register-new-password-container">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  className="register-new-input"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="register-new-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <span className="register-new-error">{validationErrors.password}</span>
              )}
            </div>

            <div className="register-new-form-group">
              <label htmlFor="confirmPassword" className="register-new-label">
                Confirm Password
              </label>
              <div className="register-new-password-container">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="register-new-input"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="register-new-password-toggle"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <span className="register-new-error">{validationErrors.confirmPassword}</span>
              )}
            </div>

            <div className="register-new-form-group">
              <label className="register-new-label">Account Type</label>
              <select
                value={formData.type}
                onChange={(e) => handleTypeSelect(e.target.value)}
                className="register-new-select"
                disabled={isLoading}
              >
                {accountTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="register-new-submit-button"
              disabled={
                isLoading ||
                !formData.email ||
                !formData.password ||
                !formData.confirmPassword
              }
            >
              {isLoading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          {/* Sign In Link */}
          <p className="register-new-switch-text">
            Already have an account?{" "}
            <button
              onClick={onSwitchToLogin}
              className="register-new-switch-link"
              disabled={isLoading}
            >
              Sign in
            </button>
          </p>

          {error && (
            <div className="register-new-error-message">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Marketing Message */}
      <div className="register-new-right">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&auto=format&fit=crop"
          alt="Background"
          className="register-new-background-image"
        />
        <div className="register-new-overlay">
          <div className="register-new-marketing">
            <h2 className="register-new-marketing-title">
              Join thousands finding support through Vocrea AI
            </h2>
            <div className="register-new-marketing-description">
              <p>
                Create your account to access personalized emotional support from real professionals.
              </p>
              <p>
                Our voice-first platform makes it easy to connect anytime, anywhere.
              </p>
            </div>
            <div className="register-new-features">
              <div className="register-new-feature-card">
                <div className="register-new-feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <div className="register-new-feature-text">
                  <div className="register-new-feature-title">Secure</div>
                  <div className="register-new-feature-subtitle">& Private</div>
                </div>
              </div>
              <div className="register-new-feature-card">
                <div className="register-new-feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                <div className="register-new-feature-text">
                  <div className="register-new-feature-title">Caring</div>
                  <div className="register-new-feature-subtitle">Professionals</div>
                </div>
              </div>
              <div className="register-new-feature-card">
                <div className="register-new-feature-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <polyline points="17 11 19 13 23 9" />
                  </svg>
                </div>
                <div className="register-new-feature-text">
                  <div className="register-new-feature-title">Easy</div>
                  <div className="register-new-feature-subtitle">Setup</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="register-new-modal-overlay">
          <div className="register-new-modal-content">
            <h3>Just One More Step!</h3>
            <p>
              Since you're joining as a{" "}
              {formData.type === "therapist" ? "Therapist" : "Career Coach"},
              we'd love to learn more about your professional background. This helps us connect you with the right clients!
            </p>
            <div className="register-new-modal-buttons">
              <button
                className="register-new-modal-button continue"
                onClick={handleContinueToQuestionnaire}
              >
                Continue
              </button>
              <button
                className="register-new-modal-button close"
                onClick={() => setShowConfirmDialog(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterScreenNew;
